import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SpecialZoomLevel, Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { getParasha } from '../utils/userSelections';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const WORKER_URL = `https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js`;

const TEXT_DARK = '#1A1A2E';
const BORDER_LIGHT = '#D1D5DB';

const TikunKorimPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language?.startsWith('he');

  const parasha = getParasha();
  const pdfUrl = parasha
    ? `${process.env.PUBLIC_URL ?? ''}/tikun-korim/${encodeURIComponent(parasha.english)}.pdf`
    : null;

  const zoomPluginInstance = zoomPlugin();
  const { ZoomIn, ZoomOut, ZoomPopover } = zoomPluginInstance;

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: () => [],
  });

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        background: '#f9fafb',
        fontFamily: "var(--kol-font-sans, 'Inter', system-ui, sans-serif)",
        color: TEXT_DARK,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <header
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: `1px solid ${BORDER_LIGHT}`,
          background: '#FDF9F3',
        }}
      >
        <button
          type="button"
          onClick={() => navigate('/user-portal')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            background: 'white',
            border: `1px solid ${BORDER_LIGHT}`,
            borderRadius: 14,
            fontSize: 15,
            fontWeight: 600,
            color: TEXT_DARK,
            cursor: 'pointer',
          }}
        >
          {isHebrew ? (
            <>
              <span aria-hidden>→</span> {t('back', 'Back')}
            </>
          ) : (
            <>
              <span aria-hidden>←</span> {t('back', 'Back')}
            </>
          )}
        </button>

        {/* Zoom controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <ZoomOut>
            {(props) => (
              <button
                type="button"
                onClick={props.onClick}
                style={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'white',
                  border: `1px solid ${BORDER_LIGHT}`,
                  borderRadius: 10,
                  fontSize: 20,
                  cursor: 'pointer',
                  color: TEXT_DARK,
                }}
                aria-label={t('zoom_out', 'Zoom out') as string}
              >
                −
              </button>
            )}
          </ZoomOut>
          <ZoomPopover />
          <ZoomIn>
            {(props) => (
              <button
                type="button"
                onClick={props.onClick}
                style={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'white',
                  border: `1px solid ${BORDER_LIGHT}`,
                  borderRadius: 10,
                  fontSize: 20,
                  cursor: 'pointer',
                  color: TEXT_DARK,
                }}
                aria-label={t('zoom_in', 'Zoom in') as string}
              >
                +
              </button>
            )}
          </ZoomIn>
        </div>
      </header>

      {/* Content */}
      {pdfUrl ? (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Worker workerUrl={WORKER_URL}>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Viewer
                fileUrl={pdfUrl}
                plugins={[defaultLayoutPluginInstance, zoomPluginInstance]}
                defaultScale={SpecialZoomLevel.PageWidth}
                renderLoader={() => (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      padding: 24,
                    }}
                  >
                    {t('loading', 'Loading')}…
                  </div>
                )}
              />
            </div>
          </Worker>
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            padding: 24,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 48 }}>📖</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: TEXT_DARK }}>
            {t('tikun_korim_no_parasha', 'No parasha selected')}
          </div>
          <div style={{ fontSize: 14, color: '#6B7280' }}>
            {t(
              'tikun_korim_choose_first',
              'Please choose a parasha first to view its Tikun Korim.',
            )}
          </div>
          <button
            type="button"
            onClick={() => navigate('/choose-parasha')}
            style={{
              marginTop: 8,
              padding: '12px 24px',
              background: '#3B7BF4',
              color: 'white',
              border: 'none',
              borderRadius: 14,
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {t('choose_parasha', 'Choose Parasha')}
          </button>
        </div>
      )}
    </div>
  );
};

export default TikunKorimPage;
